import { FiChevronRight, FiGrid } from "react-icons/fi";
import { formatNaira } from "../../utils/dashboardFormat";
import { Notice, PanelHead, titleCase } from "./parts";
import { sectionState } from "./profileModel";

const amountOf = (n) => Number(n) || 0;

/**
 * Savings, shares and other contributions all show the same thing: one row per category with the
 * total, progress to any threshold, and a way into the payments behind it.
 */
export default function CategoryPanel({
  groups = [],
  total = 0,
  title,
  summary,
  tone = "green",
  icon: Icon = FiGrid,
  totalLabel = "Total",
  emptyTitle,
  emptyText,
  missingTitle,
  onOpen,
  onRetry,
}) {
  const state = sectionState({ rows: groups.length, total });

  if (state === "empty") {
    return (
      <Notice title={emptyTitle}>{emptyText}</Notice>
    );
  }
  if (state === "missing") {
    return (
      <Notice tone="warn" title={missingTitle} actionLabel={onRetry ? "Reload" : undefined} onAction={onRetry}>
        This member has {formatNaira(total)} on record, but the individual payments didn't come through.
        Reload the page; if it keeps happening, tell support.
      </Notice>
    );
  }

  const payments = groups.reduce((sum, g) => sum + (Array.isArray(g.entries) ? g.entries.length : 0), 0);

  return (
    <div className={`tone-${tone}`}>
      <PanelHead
        title={title}
        summary={summary}
        stats={[
          { label: totalLabel, value: formatNaira(total), tone },
          { label: "Payments", value: String(payments) },
          { label: groups.length === 1 ? "Category" : "Categories", value: String(groups.length) },
        ]}
      />
      <div className="mp-scroll">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Category</th>
              <th className="num">Total</th>
              <th>Target</th>
              <th className="num">Payments</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {groups.map((g, i) => {
              const threshold = amountOf(g.threshold);
              const paid = amountOf(g.total_amount);
              const pct = threshold > 0 ? Math.min(100, Math.round((paid / threshold) * 100)) : null;
              const entries = Array.isArray(g.entries) ? g.entries : [];
              return (
                <tr key={g.category_id ?? i}>
                  <td>
                    <span className="mp-cat">
                      <span className="mp-chip-ico sm">
                        <Icon size={14} aria-hidden="true" />
                      </span>
                      {titleCase(g.category_name || "Unknown")}
                    </span>
                  </td>
                  <td className="num">{formatNaira(paid)}</td>
                  <td>
                    {pct === null ? (
                      <span className="mp-mute">No target set</span>
                    ) : (
                      <>
                        <span className="sub">
                          {pct}% of {formatNaira(threshold)}
                        </span>
                        <div className="mp-bar" role="img" aria-label={`${pct}% of target`}>
                          <span style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    )}
                  </td>
                  <td className="num"><span className="mp-count-pill">{entries.length}</span></td>
                  <td className="num">
                    {entries.length > 0 ? (
                      <button type="button" className="mp-btn sm" onClick={() => onOpen(g)}>
                        View <FiChevronRight size={14} aria-hidden="true" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
